import {LitElement, html, css, svg, type TemplateResult} from 'lit';
import {customElement, state} from 'lit/decorators.js';
import {morphedRead} from '../../auth/morphed-client.js';
import {userSignal} from '../../auth/auth.store.js';

interface BienvenidaItem {
	titulo: string;
	descripcion: string;
	icon?: string;
}
interface Bienvenida {
	heroTitle?: string;
	heroSubtitle?: string;
	mensaje?: string;
	items?: BienvenidaItem[];
}

const ICONS: Record<string, TemplateResult> = {
	code: svg`<polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>`,
	cube: svg`<path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>`,
	layers: svg`<path d="m12 2 9 5-9 5-9-5 9-5Z"/><path d="m3 12 9 5 9-5"/><path d="m3 17 9 5 9-5"/>`,
	leaf: svg`<path d="M11 20A7 7 0 0 1 4 13c0-5 5-9 16-9 0 11-4 16-9 16Z"/><path d="M4 20c2-4 6-7 9-8"/>`,
	mail: svg`<rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>`,
	qr: svg`<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3v3h-3z"/><path d="M20 14v3"/><path d="M14 20h3"/><path d="M20 20v1"/>`,
	scan: svg`<path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><path d="M7 12h10"/>`,
	shield: svg`<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/>`,
	zap: svg`<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>`,
	users: svg`<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>`,
	settings: svg`<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"/>`,
	image: svg`<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.5-3.5L9 20"/>`,
	calendar: svg`<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>`,
	check: svg`<path d="M20 6 9 17l-5-5"/>`,
	lock: svg`<rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>`,
	sparkles: svg`<path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3Z"/><path d="M19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9L19 15Z"/>`,
};

function renderIcon(name?: string): unknown {
	const inner = ICONS[name || ''] || ICONS.sparkles;
	return html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="width:1em;height:1em;display:block">${inner}</svg>`;
}

/** Vista de Bienvenida default del admin (para usuarios que no ven el dev log).
 *  Lee la entidad `Clam Bienvenida` (singleton) y renderiza hero + tarjetas. */
@customElement('clam-bienvenida')
export class ClamBienvenida extends LitElement {
	static styles = css`
		:host {
			display: block;
			font-family: var(--clam-font, 'Inter', system-ui, sans-serif);
			color: var(--clam-text, #333);
			padding: var(--clam-space-6, 24px);
			max-width: 1120px;
			margin: 0 auto;
			box-sizing: border-box;
		}
		.hero {
			position: relative;
			overflow: hidden;
			background-color: var(--clam-darkgray, #424244);
			color: var(--clam-white, #fdfdfd);
			border-radius: var(--clam-radius-lg, 12px);
			padding: var(--clam-space-7, 32px) var(--clam-space-6, 24px);
			margin-bottom: var(--clam-space-7, 32px);
			box-shadow: 0 8px 30px rgba(0, 0, 0, 0.22);
		}
		.hero::before {
			content: '';
			position: absolute;
			inset: 0;
			background: radial-gradient(circle at 88% 12%, rgba(107, 187, 36, 0.38), transparent 52%);
			pointer-events: none;
		}
		.hero::after {
			content: '';
			position: absolute;
			inset: 0;
			background-image: radial-gradient(rgba(255, 255, 255, 0.07) 1px, transparent 1px);
			background-size: 18px 18px;
			pointer-events: none;
			opacity: 0.6;
		}
		.hero > * { position: relative; z-index: 1; }
		.hero__title { margin: 0 0 var(--clam-space-2, 8px); font-size: var(--clam-text-2xl, 24px); font-weight: 700; letter-spacing: -0.01em; }
		.hero__subtitle { margin: 0 0 var(--clam-space-3, 12px); font-size: var(--clam-text-base, 14px); opacity: 0.85; font-weight: 600; }
		.hero__msg { margin: 0; font-size: var(--clam-text-base, 14px); opacity: 0.8; line-height: 1.55; max-width: 70ch; }
		.hero__user { margin-top: var(--clam-space-4, 16px); font-size: var(--clam-text-xs, 11px); opacity: 0.6; }
		.grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 230px), 1fr)); gap: var(--clam-space-4, 16px); }
		.card {
			position: relative;
			background: var(--clam-surface, #fff);
			border: 1px solid var(--clam-border, #e2e8f0);
			border-top: 3px solid var(--clam-green, #5a9d1e);
			border-radius: var(--clam-radius-md, 8px);
			padding: var(--clam-space-4, 16px);
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
			display: flex; flex-direction: column; gap: var(--clam-space-2, 8px);
			transition: transform 0.18s ease, box-shadow 0.18s ease;
		}
		.card:hover { transform: translateY(-3px); box-shadow: 0 10px 24px rgba(0, 0, 0, 0.1); }
		.card__icon { width: 38px; height: 38px; border-radius: 10px; background: rgba(90, 157, 30, 0.12); color: var(--clam-green, #5a9d1e); display: flex; align-items: center; justify-content: center; font-size: 19px; }
		.card__title { margin: 0; font-size: var(--clam-text-base, 14px); font-weight: 700; }
		.card__desc { margin: 0; font-size: var(--clam-text-sm, 13px); color: var(--clam-text-muted, #6d6e71); line-height: 1.5; }
		.empty { padding: var(--clam-space-6, 24px); text-align: center; color: var(--clam-light-gray, #898a8c); font-size: var(--clam-text-sm, 13px); }
	`;

	@state() private _data: Bienvenida | null = null;
	@state() private _loading = true;

	connectedCallback(): void {
		super.connectedCallback();
		void this._load();
	}

	private async _load(): Promise<void> {
		try {
			const rows = await morphedRead<Bienvenida>('Clam Bienvenida', {options: {limit: 1}});
			this._data = rows[0] ?? null;
		} catch {
			this._data = null;
		} finally {
			this._loading = false;
			this.requestUpdate();
		}
	}

	render(): unknown {
		if (this._loading) return html`<div class="empty">Cargando…</div>`;
		const d = this._data;
		const email = userSignal.value?.email ?? '';
		const items = d?.items ?? [];
		return html`
			<header class="hero">
				<h2 class="hero__title">${d?.heroTitle ?? 'Bienvenido a CLAM Veracruz'}</h2>
				${d?.heroSubtitle ? html`<p class="hero__subtitle">${d.heroSubtitle}</p>` : null}
				${d?.mensaje ? html`<p class="hero__msg">${d.mensaje}</p>` : null}
				${email ? html`<p class="hero__user">Sesión: ${email}</p>` : null}
			</header>
			${items.length
				? html`<div class="grid">
					${items.map((it) => html`
						<div class="card">
							<div class="card__icon">${renderIcon(it.icon)}</div>
							<h3 class="card__title">${it.titulo}</h3>
							<p class="card__desc">${it.descripcion}</p>
						</div>
					`)}
				</div>`
				: html`<p class="empty">Sin tarjetas. Edita "Contenido Bienvenida" para añadir.</p>`}
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'clam-bienvenida': ClamBienvenida;
	}
}
